import { Link } from 'react-router-dom'
import { ArrowLeft } from 'lucide-react'
import { Card } from '@/components/ui'
import { useContact } from '@/hooks/useContact'
import { Logo } from '@/components/Logo'

/**
 * Integritetspolicyn.
 *
 * Öppen sida, utan inloggning. Den som funderar på att skapa konto ska kunna
 * läsa vad det innebär innan hen gör det – en policy bakom en inloggning är
 * ingen policy.
 *
 * Skriven som till en förälder i föreningen, precis som hjälpen. GDPR kräver
 * att informationen är "klar och tydlig" och på ett enkelt språk; juridiksvenska
 * uppfyller inte det kravet bara för att den är noggrann.
 *
 * Varje uppgift om lagringstid här motsvarar något som koden faktiskt gör.
 * Står det att granskningsloggen gallras efter två år är det för att
 * gdprCleanup gör det. En policy som lovar mer än koden håller är värre än
 * ingen policy alls.
 */
export function PrivacyPage() {
  const contact = useContact()

  return (
    <main className="mx-auto flex min-h-dvh w-full max-w-2xl flex-col gap-5 px-4 py-8">
      <div className="flex justify-center">
        <Logo size={52} tone="ink" subtitle="Så hanterar vi dina uppgifter" />
      </div>

      <Link
        to="/"
        className="tap-safe inline-flex items-center gap-2 self-start text-sm font-semibold text-brand-ink"
      >
        <ArrowLeft size={16} aria-hidden />
        Tillbaka
      </Link>

      <Card className="space-y-6 p-5 text-sm leading-relaxed">
        <p className="text-muted">
          Senast ändrad 14 oktober 2026.
        </p>

        <section className="space-y-2">
          <h2 className="font-display text-lg font-extrabold">Vem som ansvarar</h2>
          <p>
            Lira BK är personuppgiftsansvarig för Lirahäftet. Det betyder att det är föreningen
            som bestämmer varför och hur dina uppgifter används, och som du vänder dig till om du
            har frågor.
          </p>
          <div className="rounded-card bg-surface-2 p-4">
            <p className="font-semibold">Lira BK</p>
            <p className="text-muted">Organisationsnummer {contact.orgNumber}</p>
            <p className="text-muted">Föreningsnummer {contact.federationNumber}</p>
            <p className="mt-2 font-semibold">
              {contact.dataContactName}, {contact.dataContactRole}
            </p>
            <p className="text-muted">
              <a href={`mailto:${contact.dataContactEmail}`} className="underline">
                {contact.dataContactEmail}
              </a>
              {' · '}
              <a href={`tel:${contact.dataContactPhone.replace(/[^+\d]/g, '')}`} className="underline">
                {contact.dataContactPhone}
              </a>
            </p>
          </div>
        </section>

        <section className="space-y-3">
          <h2 className="font-display text-lg font-extrabold">Vad vi sparar, och varför</h2>
          <p>
            Vi samlar in så lite som möjligt. Vi frågar aldrig efter personnummer, adress eller
            betalkortsuppgifter, vi har ingen besöksmätning, och ingenting lämnar appen till
            någon annan än den som lagrar databasen åt oss.
          </p>
          <p>
            Häftet säljs i den här upplagan på papper: du får en lapp med en kod och aktiverar
            den här. Därför frågar vi varken efter ditt namn, ditt barns namn eller något om en
            beställning. Den dagen föreningen öppnar för beställning i appen uppdateras den här
            sidan innan det sker.
          </p>

          <div className="overflow-x-auto">
            <table className="w-full min-w-[34rem] border-collapse text-left">
              <thead>
                <tr className="border-b border-border text-xs tracking-wide text-muted uppercase">
                  <th scope="col" className="py-2 pr-3 font-bold">Uppgift</th>
                  <th scope="col" className="py-2 pr-3 font-bold">Varför</th>
                  <th scope="col" className="py-2 pr-3 font-bold">Rättslig grund</th>
                  <th scope="col" className="py-2 font-bold">Hur länge</th>
                </tr>
              </thead>
              <tbody className="align-top">
                <tr className="border-b border-border">
                  <td className="py-3 pr-3 font-semibold">E-postadress och lösenord</td>
                  <td className="py-3 pr-3 text-muted">
                    För att du ska kunna logga in och nå ditt häfte från flera enheter.
                  </td>
                  <td className="py-3 pr-3 text-muted">Avtal</td>
                  <td className="py-3 text-muted">
                    Tills du raderar kontot, eller två år efter din senaste inloggning. Då
                    markeras kontot och raderas automatiskt en månad senare. Loggar du in under
                    tiden börjar klockan om.
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 pr-3 font-semibold">Vilka koder du aktiverat</td>
                  <td className="py-3 pr-3 text-muted">
                    För att häftet ska vara ditt och inte gå att aktivera av någon annan.
                  </td>
                  <td className="py-3 pr-3 text-muted">Avtal</td>
                  <td className="py-3 text-muted">Samma som kontot.</td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 pr-3 font-semibold">Vilka rabatter du använt</td>
                  <td className="py-3 pr-3 text-muted">
                    För att samma rabatt bara ska gå att använda en gång per häfte, och för att
                    kunna visa dig vad du sparat.
                  </td>
                  <td className="py-3 pr-3 text-muted">Avtal</td>
                  <td className="py-3 text-muted">
                    Kopplingen till dig tas bort när kontot raderas. Själva inlösen räknas kvar,
                    utan namn, i föreningens statistik.
                  </td>
                </tr>
                <tr className="border-b border-border">
                  <td className="py-3 pr-3 font-semibold">Felrapporter du skickar</td>
                  <td className="py-3 pr-3 text-muted">
                    För att kunna laga det som gick sönder. Rapporten innehåller det du skriver,
                    din adress, vilken sida du var på och vilken webbläsare du använder.
                  </td>
                  <td className="py-3 pr-3 text-muted">Berättigat intresse</td>
                  <td className="py-3 text-muted">Två år, eller tills du raderar kontot.</td>
                </tr>
                <tr>
                  <td className="py-3 pr-3 font-semibold">Logg över vad administratörer gjort</td>
                  <td className="py-3 pr-3 text-muted">
                    För att föreningen ska kunna gå tillbaka och se vem som ändrat vad, om något
                    blivit fel. Loggen handlar om dem som sköter häftet – att du aktiverat ditt
                    häfte eller använt en rabatt skrivs inte dit.
                  </td>
                  <td className="py-3 pr-3 text-muted">Berättigat intresse</td>
                  <td className="py-3 text-muted">Två år.</td>
                </tr>
              </tbody>
            </table>
          </div>
        </section>

        <section className="space-y-2">
          <h2 className="font-display text-lg font-extrabold">Ingenting om barnen</h2>
          <p>
            Appen innehåller inga uppgifter om spelarna. Vi frågar inte efter deras namn, ålder
            eller lag, och det finns ingen ruta där någon annan kan skriva in dem heller. Det som
            står i appen är kopplat till den vuxna som skapat kontot.
          </p>
          <p>
            Att föreningen förstås vet vilka barn som spelar är en annan sak, och den uppgiften
            bor i medlemsregistret – inte här.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-display text-lg font-extrabold">Vilka mer som ser uppgifterna</h2>
          <p>
            Inom föreningen: din lagledare ser sitt eget lags koder. Administratörer ser mer, men
            bara det som behövs för att sköta häftet.
          </p>
          <p>
            Utanför föreningen anlitar vi <strong>en enda</strong> leverantör:
          </p>
          <ul className="list-disc space-y-1 pl-5 text-muted">
            <li>
              <strong className="text-ink">Google (Firebase)</strong> lagrar databasen och sköter
              inloggningen. Databasen, bilderna och all beräkning ligger på servrar i Belgien.
              Själva inloggningen är ett undantag: din adress och ditt lösenord hanteras av Googles
              globala inloggningstjänst, som inte går att låsa till Europa, och kan därför
              behandlas utanför EU.
            </li>
          </ul>
          <p>
            Google är ett amerikanskt bolag och omfattas av EU:s och USA:s dataskyddsramverk,
            vilket gör överföringen tillåten. Vi skickar inga mejl, vi anlitar ingen
            mejlleverantör, och vi säljer aldrig uppgifter vidare.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-display text-lg font-extrabold">Säkerhetskopior</h2>
          <p>
            Hela databasen kopieras varje natt och sparas i 30 dagar, så att ingenting går
            förlorat om något havererar. Kopiorna ligger hos samma leverantör, i Belgien, och
            raderas av sig själva när tiden gått ut.
          </p>
          <p>
            Det betyder en sak värd att veta: raderar du ditt konto försvinner det ur appen
            direkt, men finns kvar i nattens kopior i upp till en månad innan också de är borta.
            Kopiorna används bara för att återställa efter ett haveri, aldrig för att titta på
            enskilda personer.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-display text-lg font-extrabold">Vad som sparas i din telefon</h2>
          <p>
            Appen sparar en del i webbläsaren för att fungera: din inloggning, ditt val av ljust
            eller mörkt läge, och en kopia av häftet så att du kan visa erbjudanden även utan
            täckning i en butikskällare.
          </p>
          <p>
            Vi använder <strong>inga kakor alls</strong> för spårning, reklam eller mätning, och
            därför finns heller ingen sådan ruta att klicka bort. Det finns inget skräpskydd som
            tittar på hur din enhet beter sig, ingen besöksstatistik och ingen tredje part som
            läser med. Det som sparas stannar i din telefon och behövs för att appen ska fungera.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-display text-lg font-extrabold">Dina rättigheter</h2>
          <p>Du har rätt att:</p>
          <ul className="list-disc space-y-1 pl-5 text-muted">
            <li>få veta vilka uppgifter vi har om dig, och få en kopia</li>
            <li>få felaktiga uppgifter rättade</li>
            <li>få dina uppgifter raderade</li>
            <li>invända mot att vi behandlar dem, eller begära att vi begränsar behandlingen</li>
            <li>få ut dina uppgifter i ett format du kan ta med dig någon annanstans</li>
          </ul>
          <p>
            Du behöver inte be om kopian. Under <strong>Mitt konto</strong> står rutan{' '}
            <strong>Allt vi har om dig</strong>: adressen, när kontot skapades, varje rabatt du
            använt med företag, belopp och datum, dina häften och de felrapporter du skickat. Det
            är hela listan, synlig när du vill och utan att någon behöver handlägga den.
          </p>
          <p>
            Det enklaste sättet att radera allt är knappen <strong>Radera kontot</strong> längst ned
            på Mitt konto. Den kopplar bort dig från allt vi sparat – dina koder och din historik –
            och tar bort inloggningen. Det går inte att ångra.
          </p>
          <p>
            Två saker överlever med flit, och det är ärligare att skriva det än att låta "allt"
            betyda nästan allt: en rad i granskningsloggen om att raderingen skedde, så att
            föreningen kan svara på frågan efteråt, och nattens säkerhetskopior under den månad de
            lever.
          </p>
          <p>
            För allt annat, hör av dig till ordföranden ovan. Vi svarar inom en månad.
          </p>
          <p>
            Tycker du att vi hanterar dina uppgifter fel har du rätt att klaga till{' '}
            <a
              href="https://www.imy.se"
              target="_blank"
              rel="noreferrer"
              className="font-semibold text-brand-ink underline"
            >
              Integritetsskyddsmyndigheten
            </a>
            . Men hör gärna av dig till oss först – det brukar gå fortare.
          </p>
        </section>

        <section className="space-y-2">
          <h2 className="font-display text-lg font-extrabold">Två saker vi inte gör</h2>
          <p>
            Vi fattar <strong>inga automatiska beslut</strong> om dig, och vi profilerar dig inte.
            Statistiken vi tar fram handlar om hur häftet används i stort – vilka erbjudanden som
            är populära, när på dygnet de löses in – aldrig om enskilda personer.
          </p>
          <p>
            Det enda som sker av sig självt är att ett konto som stått oanvänt i två år markeras
            och raderas en månad senare. Det står under Vad vi sparar ovan, och det går att
            avbryta genom att logga in.
          </p>
        </section>
      </Card>
    </main>
  )
}
